import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const FORM_URL = 'https://readdy.ai/api/form/d8tt7tpd9m2hsn3jskqg';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setStatus('success');
      setEmail('');
      setTimeout(() => setStatus('idle'), 3000);
      return;
    }

    if (!email.trim()) return;

    setStatus('loading');
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      setStatus('success');
      setEmail('');
      setTimeout(() => setStatus('idle'), 5000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <footer className="bg-background-100 border-t border-background-200/70">
      <div className="w-full px-4 md:px-6 py-14 md:py-16">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          <div className="lg:w-[40%]">
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 leading-tight">
              Stay Updated<br />With ACBuySpreadsheet
            </h3>
            <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
              Get the latest spreadsheet tips, category updates, and workflow guides delivered straight to your inbox. No spam, ever.
            </p>

            <form
              onSubmit={handleSubscribe}
              data-readdy-form
              className="mt-8"
            >
              <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
                Enter your email
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="flex-1 bg-transparent border-b border-foreground-300 px-1 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="px-6 py-2.5 border border-foreground-800 text-foreground-800 rounded-md text-sm font-semibold hover:bg-foreground-800 hover:text-background-50 transition-colors whitespace-nowrap disabled:opacity-50"
                >
                  {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
                </button>
              </div>

              <div className="newsletter-website-alt-wrapper">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              </div>

              {status === 'success' && (
                <p className="mt-3 text-sm text-accent-700 font-medium">Thanks for subscribing! Check your inbox soon.</p>
              )}
              {status === 'error' && (
                <p className="mt-3 text-sm text-red-600">Something went wrong. Please try again.</p>
              )}
            </form>
          </div>

          <div className="lg:w-[60%]">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-3">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors py-1 whitespace-nowrap"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-background-200/70 flex flex-col sm:flex-row items-center justify-between gap-3">
          <Link to="/" className="text-sm text-foreground-500 hover:text-foreground-800 transition-colors">
            Home
          </Link>
          <p className="text-xs text-foreground-400">
            acbuyspreadsheets.space
          </p>
        </div>
      </div>
    </footer>
  );
}